import React, { useState, useCallback, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Stack, Checkbox, mergeStyles } from '@fluentui/react';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { notificationApi } from '@bingads-webui-campaign/notification-center';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { DefaultButton, PrimaryButton, StepWrapper, UiBlocker } from '../components';
import { useHideCalloutPreference, useAutoBiddingFac } from '../hooks';
import { FAC_STATUS, AUTO_BIDDING_FEATURE_IDS } from '../constants';
import CouponIcon from '../../media/CouponIcon.svg';

const frontdoorStyles = mergeStyles({
  width: 600,
});

const descriptionStyles = mergeStyles({
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: 20,
});

const couponImageStyles = mergeStyles({
  marginTop: 24,
  marginBottom: 56,
});

const nextButtonStyles = mergeStyles({
  margin: 8,
});

export const CouponFrontdoor = (props) => {
  const {
    coupon,
    onNext,
    onClose,
  } = props;

  const i18n = useContext(I18nContext);

  const serverContext = useContext(ServerContext);
  const { CurrentUser, CurrentAccount } = serverContext;
  const { hideCalloutForEver, hideCalloutFor7Days } = useHideCalloutPreference(CurrentUser.Id, CurrentAccount.Id, coupon);
  const { getFormattedCurrencyValue, activateCoupon } = useAutoBiddingFac();

  const [doNotShowAgainCheck, setDoNotShowAgainCheck] = useState(false);
  const [isActivatingCoupon, setIsActivatingCoupon] = useState(false);

  const onNextClick = useCallback(() => {
    if (coupon.Status === FAC_STATUS.NOT_IN_PROGRESS) {
      setIsActivatingCoupon(true);
      activateCoupon(coupon)
        .then(onNext)
        .catch(() => {
          setIsActivatingCoupon(false);
          notificationApi.showWarning(i18n.getString(_TL_('Microsoft Advertising problem, try again later.')));
        });
    } else {
      onNext();
    }
  }, [activateCoupon, coupon, i18n, onNext]);

  const onCloseClick = useCallback(() => {
    onClose();
    if (doNotShowAgainCheck) {
      hideCalloutForEver();
    } else {
      hideCalloutFor7Days();
    }
  }, [doNotShowAgainCheck, hideCalloutFor7Days, hideCalloutForEver, onClose]);

  const formattedCouponValue = useMemo(() => getFormattedCurrencyValue(coupon.CouponValue), [coupon.CouponValue, getFormattedCurrencyValue]);
  const formattedCouponUpfrontSpend = useMemo(() => getFormattedCurrencyValue(coupon.CouponUpfrontSpend), [coupon.CouponUpfrontSpend, getFormattedCurrencyValue]);

  const descriptionText = useMemo(() => {
    switch (coupon.FeatureId) {
      case AUTO_BIDDING_FEATURE_IDS.MAX_CLICKS:
        return i18n.getString(_TL_('You can redeem {0} in advertising credit when you set a maximize clicks bid strategy and accrue {1} of spend for one or more campaign(s).'))
          .replace('{0}', formattedCouponValue)
          .replace('{1}', formattedCouponUpfrontSpend);
      case AUTO_BIDDING_FEATURE_IDS.MAX_CONVERSIONS:
        return i18n.getString(_TL_('You can redeem {0} in advertising credit when you set a maximize conversions bid strategy and accrue {1} of spend for one or more campaign(s).'))
          .replace('{0}', formattedCouponValue)
          .replace('{1}', formattedCouponUpfrontSpend);
      default:
        return '';
    }
  }, [coupon.FeatureId, formattedCouponUpfrontSpend, formattedCouponValue, i18n]);

  return (
    <StepWrapper coupon={coupon} onClose={onCloseClick}>
      <UiBlocker
        blocking={isActivatingCoupon}
        loadingText={i18n.getString(_TL_('Loading ...'))}
      >
        <div className={classNames(frontdoorStyles, 'coupon-frontdoor')}>
          <div className={descriptionStyles}>
            {descriptionText}
          </div>
          <Stack horizontalAlign="center" className={couponImageStyles}>
            <img
              alt=""
              src={CouponIcon}
            />
          </Stack>
          <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className="footer">
            <Checkbox label={i18n.getString(_TL_('Don\'t show this to me again'))} onChange={() => { setDoNotShowAgainCheck(!doNotShowAgainCheck); }} />
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
              <PrimaryButton text={i18n.getString(_TL_('Continue'))} onClick={onNextClick} className={classNames(nextButtonStyles, 'next-button')} />
              <DefaultButton text={i18n.getString(_TL_('Close'))} onClick={onCloseClick} className="close-button" />
            </Stack>
          </Stack>
        </div>
      </UiBlocker>
    </StepWrapper>);
};

CouponFrontdoor.propTypes = {
  coupon: PropTypes.shape({
    CouponUpfrontSpend: PropTypes.number.isRequired,
    CouponValue: PropTypes.number.isRequired,
    Status: PropTypes.number.isRequired,
    FeatureId: PropTypes.number.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};
